import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Stack, Chip, CircularProgress, Alert,
    Grid, Paper, Button
} from '@mui/material';
import { Package, FileText, ExternalLink, Tag, Star, CheckCircle, XCircle } from 'lucide-react';
import { getRetreatById } from '../../../Services/Retreats/RetreatApis';
import { getEventTypeColor, getStatusColor, getExperienceColor } from '../RetreatDetailsUtils';

// ─── Shared primitives ────────────────────────────────────────────────────────

const SectionTitle = ({ icon: Icon, children }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Icon size={15} color="#FFBD65" strokeWidth={2.2} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B', letterSpacing: '0.02em' }}>
            {children}
        </Typography>
    </Box>
);

const InfoRow = ({ label, children, noBorder }) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'flex-start',
            py: 0.9,
            borderBottom: noBorder ? 'none' : '1px solid #F1F5F9',
            gap: { xs: 0.25, sm: 0 },
        }}
    >
        <Typography
            variant="caption"
            sx={{ color: '#94A3B8', fontWeight: 500, minWidth: { sm: 150 }, pt: { sm: '2px' }, flexShrink: 0 }}
        >
            {label}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            {typeof children === 'string' || typeof children === 'number' ? (
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155' }}>{children}</Typography>
            ) : children}
        </Box>
    </Box>
);

const InfoCard = ({ children, sx }) => (
    <Paper
        elevation={0}
        sx={{ border: '1px solid #E8EEF4', borderRadius: '10px', bgcolor: '#FFFFFF', overflow: 'hidden', ...sx }}
    >
        {children}
    </Paper>
);

/** Inline chip row */
const ChipRow = ({ items, labelKey, fallbackKey, chipSx }) => {
    if (!items?.length) return null;
    return (
        <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {items.map((item, idx) => (
                <Chip
                    key={idx}
                    label={item[labelKey] || item[fallbackKey] || String(item)}
                    size="small"
                    sx={{ height: 22, fontSize: '0.73rem', fontWeight: 500, borderRadius: '6px', ...chipSx }}
                />
            ))}
        </Stack>
    );
};

/** Styled HTML content box */
const HtmlContent = ({ html }) => (
    <Box
        sx={{
            color: '#475569', fontSize: '0.85rem', lineHeight: 1.65,
            '& p': { mb: 1 },
            '& ul, & ol': { pl: '1.2rem', mb: 1, '& li': { mb: 0.5 } },
            '& *': { fontFamily: 'inherit !important', fontSize: 'inherit !important', color: 'inherit !important' },
        }}
        dangerouslySetInnerHTML={{ __html: html }}
    />
);

const cardPx = { xs: 2, sm: 2.5 };

// ─── Main component ───────────────────────────────────────────────────────────

const RetreatDetailsTab = ({ retreat, onDetailsLoaded }) => {
    const [retreatDetails, setRetreatDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            if (!retreat?.id) return;
            try {
                setLoading(true);
                const response = await getRetreatById(retreat.id);
                if (response?.success) {
                    setRetreatDetails(response.data);
                    if (onDetailsLoaded) {
                        onDetailsLoaded(response.data);
                    }
                } else {
                    setError('Failed to fetch retreat details.');
                }
            } catch (err) {
                console.error('Error fetching retreat details:', err);
                setError('An error occurred while fetching retreat details.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [retreat?.id, onDetailsLoaded]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={28} sx={{ color: '#FFBD65' }} />
            </Box>
        );
    }

    if (error) return <Alert severity="error" sx={{ borderRadius: '8px' }}>{error}</Alert>;

    if (!retreatDetails) {
        return (
            <InfoCard sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">No details available for this retreat.</Typography>
            </InfoCard>
        );
    }

    const visibilityPlan = retreatDetails.visibilityPlans || retreatDetails.visibilityPlan;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* ── 1. Overview badges ────────────────────────────────────────── */}
            <InfoCard>
                <Box sx={{ px: cardPx, py: 1.5, borderBottom: '1px solid #F1F5F9' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B', mb: 0.5 }}>
                        {retreat.title || 'Untitled Retreat'}
                    </Typography>
                    {(retreat.slug || retreatDetails.slug || retreat.center?.slug) && (
                        <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 0.5, fontSize: '0.8rem' }}>
                            <Box component="span" sx={{ fontWeight: 600, color: '#475569' }}>Slug:</Box> {retreat.slug || retreatDetails.slug || retreat.center?.slug}
                        </Typography>
                    )}
                    <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1 }}>
                        {retreat.eventType && (
                            <Chip label={retreat.eventType} size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, ...getEventTypeColor(retreat.eventType) }} />
                        )}
                        {retreat.status && (
                            <Chip label={retreat.status} size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, ...getStatusColor(retreat.status) }} />
                        )}
                        {retreat.isFeatured && (
                            <Chip label="Featured" size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: '#F0FDF4', color: '#16A34A', border: '1px solid #BBF7D0' }} />
                        )}
                        {retreat.isFeaturedHome && (
                            <Chip label="Featured Home" size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: '#EFF6FF', color: '#2563EB', border: '1px solid #BFDBFE' }} />
                        )}
                        {retreat.retreatInvoiceType && (
                            <Chip label={`Invoice: ${retreat.retreatInvoiceType.toUpperCase()}`} size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: '#F8FAFC', color: '#475569', border: '1px solid #E2E8F0' }} />
                        )}
                        {retreat.coordinationCharges !== undefined && retreat.coordinationCharges !== null && retreat.coordinationCharges !== '' && (
                            <Chip label={`Coordination: ${retreat.coordinationCharges}`} size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: '#FFFBEB', color: '#B45309', border: '1px solid #FDE68A' }} />
                        )}
                    </Stack>
                </Box>
                <Box sx={{ px: cardPx, py: 1.5 }}>
                    {(retreat.slug || retreatDetails.slug || retreat.center?.slug) && (
                        <InfoRow label="Slug">
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155' }}>
                                {retreat.slug || retreatDetails.slug || retreat.center?.slug}
                            </Typography>
                        </InfoRow>
                    )}
                    {retreatDetails.experience?.length > 0 && (
                        <InfoRow label="Experience Level">
                            <Stack direction="row" flexWrap="wrap" gap={0.75}>
                                {retreatDetails.experience.map((exp, i) => (
                                    <Chip key={i} label={exp} size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, ...getExperienceColor(exp) }} />
                                ))}
                            </Stack>
                        </InfoRow>
                    )}
                    {retreatDetails.ageGroup?.length > 0 && (
                        <InfoRow label="Age Groups">
                            <ChipRow
                                items={retreatDetails.ageGroup}
                                labelKey="ageGroup"
                                chipSx={{ bgcolor: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}
                            />
                        </InfoRow>
                    )}
                    {retreatDetails.features?.length > 0 && (
                        <InfoRow label="Features">
                            <ChipRow
                                items={retreatDetails.features}
                                labelKey="featureName"
                                chipSx={{ bgcolor: '#F8FAFC', color: '#475569', border: '1px solid #E2E8F0' }}
                            />
                        </InfoRow>
                    )}
                    {retreatDetails.atmospheres?.length > 0 && (
                        <InfoRow label="Atmosphere" noBorder={!visibilityPlan}>
                            <ChipRow
                                items={retreatDetails.atmospheres}
                                labelKey="atmosphereName"
                                chipSx={{ bgcolor: '#FAF5FF', color: '#7C3AED', border: '1px solid #DDD6FE' }}
                            />
                        </InfoRow>
                    )}
                    {visibilityPlan && (
                        <InfoRow label="Visibility Plan" noBorder>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Star size={14} color="#FFBD65" fill="#FFBD65" />
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                                    {visibilityPlan.name}
                                </Typography>
                                {visibilityPlan.commissionPercentage && (
                                    <Chip
                                        label={`${visibilityPlan.commissionPercentage}% Commission`}
                                        size="small"
                                        sx={{
                                            height: 20,
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            bgcolor: '#F0FDF4',
                                            color: '#16A34A',
                                            border: '1px solid #BBF7D0',
                                            borderRadius: '4px'
                                        }}
                                    />
                                )}
                            </Stack>
                        </InfoRow>
                    )}
                </Box>
            </InfoCard>

            {/* ── 2. Summary ───────────────────────────────────────────────── */}
            {retreatDetails.summary && (
                <Box>
                    <SectionTitle icon={Star}>Summary</SectionTitle>
                    <InfoCard>
                        <Box sx={{ px: cardPx, py: 1.5 }}>
                            <HtmlContent html={retreatDetails.summary} />
                        </Box>
                    </InfoCard>
                </Box>
            )}

            {/* ── 3. Description ───────────────────────────────────────────── */}
            {retreatDetails.description && (
                <Box>
                    <SectionTitle icon={FileText}>Description</SectionTitle>
                    <InfoCard>
                        <Box sx={{ px: cardPx, py: 1.5 }}>
                            <HtmlContent html={retreatDetails.description} />
                        </Box>
                    </InfoCard>
                </Box>
            )}

            {/* ── 4. What's Special ────────────────────────────────────────── */}
            {retreatDetails.specialAbout && (
                <Box>
                    <SectionTitle icon={Tag}>What's Special</SectionTitle>
                    <InfoCard>
                        <Box sx={{ px: cardPx, py: 1.5 }}>
                            <HtmlContent html={retreatDetails.specialAbout} />
                        </Box>
                    </InfoCard>
                </Box>
            )}
            {/* ── 5.benefits ────────────────────────────────────────── */}
            {retreatDetails.benefits && (
                <Box>
                    <SectionTitle icon={Tag}>Benefits</SectionTitle>
                    <InfoCard>
                        <Box sx={{ px: cardPx, py: 1.5 }}>
                            <HtmlContent html={retreatDetails.benefits} />
                        </Box>
                    </InfoCard>
                </Box>
            )}

            {/* ── 6. Included / Not Included ───────────────────────────────── */}
            {(retreatDetails.includedPackages?.length > 0 || retreatDetails.notIncludedPackages?.length > 0) && (
                <Box>
                    <SectionTitle icon={Package}>Packages</SectionTitle>
                    <Grid container spacing={2}>
                        {retreatDetails.includedPackages?.length > 0 && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <InfoCard sx={{ height: '100%' }}>
                                    <Box sx={{ px: cardPx, py: 1, borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CheckCircle size={13} color="#16A34A" />
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#16A34A', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Included</Typography>
                                    </Box>
                                    <Box sx={{ px: cardPx, py: 1.5 }}>
                                        <Stack direction="row" flexWrap="wrap" gap={0.75}>
                                            {retreatDetails.includedPackages.map((pkg, i) => (
                                                <Chip
                                                    key={i}
                                                    label={pkg.packageName || pkg.ownPackageName || pkg.title}
                                                    size="small"
                                                    sx={{ height: 22, fontSize: '0.73rem', fontWeight: 500, bgcolor: '#F0FDF4', color: '#166534', border: '1px solid #BBF7D0', borderRadius: '6px' }}
                                                />
                                            ))}
                                        </Stack>
                                    </Box>
                                </InfoCard>
                            </Grid>
                        )}
                        {retreatDetails.notIncludedPackages?.length > 0 && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <InfoCard sx={{ height: '100%' }}>
                                    <Box sx={{ px: cardPx, py: 1, borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <XCircle size={13} color="#E11D48" />
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#E11D48', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Not Included</Typography>
                                    </Box>
                                    <Box sx={{ px: cardPx, py: 1.5 }}>
                                        <Stack direction="row" flexWrap="wrap" gap={0.75}>
                                            {retreatDetails.notIncludedPackages.map((pkg, i) => (
                                                <Chip
                                                    key={i}
                                                    label={pkg.packageName || pkg.ownPackageName || pkg.title}
                                                    size="small"
                                                    sx={{ height: 22, fontSize: '0.73rem', fontWeight: 500, bgcolor: '#FFF1F2', color: '#9F1239', border: '1px solid #FECDD3', borderRadius: '6px' }}
                                                />
                                            ))}
                                        </Stack>
                                    </Box>
                                </InfoCard>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            )}

            {/* ── 7. Attached Document ─────────────────────────────────────── */}
            {(retreatDetails.documentName || retreatDetails.documentFile) && (
                <Box>
                    <SectionTitle icon={FileText}>Attached Document</SectionTitle>
                    <InfoCard>
                        <Box
                            sx={{
                                px: cardPx, py: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                            }}
                        >
                            <Box sx={{ flexShrink: 0, bgcolor: 'rgba(255,189,101,0.12)', p: 1, borderRadius: '6px', display: 'flex', alignItems: 'center' }}>
                                <FileText size={20} color="#FFBD65" />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                {retreatDetails.documentName && (
                                    <Typography
                                        component="a"
                                        href={retreatDetails.documentName}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600, color: '#1D4ED8', display: 'block',
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            textDecoration: 'none', '&:hover': { textDecoration: 'underline' },
                                        }}
                                        title={retreatDetails.documentName}
                                    >
                                        {retreatDetails.documentName}
                                    </Typography>
                                )}
                                {retreatDetails.documentFile && (
                                    <Typography
                                        component="a"
                                        href={retreatDetails.documentFile}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="caption"
                                        sx={{
                                            display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            color: '#94A3B8', textDecoration: 'none', '&:hover': { textDecoration: 'underline', color: '#1D4ED8' },
                                        }}
                                        title={retreatDetails.documentFile}
                                    >
                                        {retreatDetails.documentFile}
                                    </Typography>
                                )}
                            </Box>
                            {retreatDetails.documentFile && (
                                <Button
                                    component="a"
                                    href={retreatDetails.documentFile}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="small"
                                    variant="outlined"
                                    startIcon={<ExternalLink size={13} />}
                                    sx={{
                                        flexShrink: 0, borderColor: '#FFBD65', color: '#FFBD65',
                                        textTransform: 'none', fontWeight: 600, borderRadius: '6px',
                                        whiteSpace: 'nowrap', px: 2,
                                    }}
                                >
                                    View
                                </Button>
                            )}
                        </Box>
                    </InfoCard>
                </Box>
            )}

            {/* ── 8. SEO Metadata ────────────────────────────────────────── */}
            {(retreatDetails.metaTitle || retreatDetails.metaDescription || retreat?.metaTitle || retreat?.metaDescription || retreatDetails.heading || retreat?.heading || retreatDetails.imageAltText || retreat?.imageAltText || retreatDetails.canonicalUrl || retreat?.canonicalUrl || retreatDetails.ogImage || retreat?.ogImage) && (
                <Box>
                    <SectionTitle icon={FileText}>SEO Metadata</SectionTitle>
                    <InfoCard>
                        <Box sx={{ px: cardPx, py: 1.5 }}>
                            {(retreatDetails.metaTitle || retreat?.metaTitle) && (
                                <InfoRow label="Meta Title">
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                                        {retreatDetails.metaTitle || retreat?.metaTitle}
                                    </Typography>
                                </InfoRow>
                            )}
                            {(retreatDetails.metaDescription || retreat?.metaDescription) && (
                                <InfoRow label="Meta Description">
                                    <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.6 }}>
                                        {retreatDetails.metaDescription || retreat?.metaDescription}
                                    </Typography>
                                </InfoRow>
                            )}
                            {(retreatDetails.heading || retreat?.heading) && (
                                <InfoRow label="Heading">
                                    <Typography variant="body2" sx={{ color: '#475569' }}>
                                        {retreatDetails.heading || retreat?.heading}
                                    </Typography>
                                </InfoRow>
                            )}
                            {(retreatDetails.imageAltText || retreat?.imageAltText) && (
                                <InfoRow label="Image Alt Text">
                                    <Typography variant="body2" sx={{ color: '#475569' }}>
                                        {retreatDetails.imageAltText || retreat?.imageAltText}
                                    </Typography>
                                </InfoRow>
                            )}
                            {(retreatDetails.canonicalUrl || retreat?.canonicalUrl) && (
                                <InfoRow label="Canonical URL">
                                    <Typography variant="body2" sx={{ color: '#475569' }}>
                                        {retreatDetails.canonicalUrl || retreat?.canonicalUrl}
                                    </Typography>
                                </InfoRow>
                            )}
                            {(retreatDetails.ogImage || retreat?.ogImage) && (
                                <InfoRow label="OG Image">
                                    <Box
                                        component="img"
                                        src={retreatDetails.ogImage || retreat?.ogImage}
                                        alt="OG Image Preview"
                                        sx={{
                                            height: 80,
                                            maxWidth: 160,
                                            objectFit: 'contain',
                                            borderRadius: '6px',
                                            border: '1px solid #E2E8F0',
                                            bgcolor: '#F8FAFC',
                                            p: 0.5
                                        }}
                                    />
                                </InfoRow>
                            )}
                            <InfoRow label="Is Index" noBorder>
                                <Chip
                                    label={(retreatDetails.isIndex === "true" || retreatDetails.isIndex === true || retreat?.isIndex === "true" || retreat?.isIndex === true || (retreatDetails.isIndex === undefined && retreat?.isIndex === undefined)) ? "True" : "False"}
                                    size="small"
                                    sx={{
                                        bgcolor: (retreatDetails.isIndex === "true" || retreatDetails.isIndex === true || retreat?.isIndex === "true" || retreat?.isIndex === true || (retreatDetails.isIndex === undefined && retreat?.isIndex === undefined)) ? "#E8F5E9" : "#FEE2E2",
                                        color: (retreatDetails.isIndex === "true" || retreatDetails.isIndex === true || retreat?.isIndex === "true" || retreat?.isIndex === true || (retreatDetails.isIndex === undefined && retreat?.isIndex === undefined)) ? "#2E7D32" : "#DC2626",
                                        fontWeight: 500,
                                        fontSize: '0.75rem',
                                        height: 22
                                    }}
                                />
                            </InfoRow>
                        </Box>
                    </InfoCard>
                </Box>
            )}

        </Box>
    );
};

export default RetreatDetailsTab;
